/**
 * repliDeLot — ce que le socle des écritures de forme décide quand l'éditeur
 * refuse un lot (#1868, spec #1858) : quoi retirer, quoi rejouer, quoi nommer.
 *
 * WHY un module pur, à part de `ecritureDeForme.ts` : le socle parle à
 * l'éditeur ; ces décisions ne dépendent que de ce que l'éditeur a répondu.
 * Un refus à l'application après un aperçu valide, un aperçu qui nomme une
 * étape étrangère au lot, une erreur sans code : ces issues ne se provoquent
 * pas à volonté sur l'éditeur réel, elles se jugent ici, sur des réponses
 * posées à la main.
 *
 * Les faits MESURÉS dont ces décisions dépendent — ce que l'aperçu voit et ne
 * voit pas, ce qu'un plan refusé laisse derrière lui — sont dans l'en-tête de
 * `ecritureDeForme.ts`, section « Quand l'éditeur refuse un lot », et nulle
 * part ailleurs.
 *
 * Le repli est agnostique de la cause : il ne retire, ne découpe et ne saute
 * que sur la foi de ce que l'éditeur répond. Seule la RAISON dite à l'agent
 * traduit cette réponse ({@link raisonDuRefus}) ; une réponse que rien
 * n'explique est rendue avec les mots de l'éditeur. Une seule décision se
 * prend ici sur le fichier, pour un défaut mesuré de l'éditeur : l'ORDRE des
 * écritures ({@link isolerLesEtapes}).
 */

import type { CanvasFormatEditorRefusalReason } from '@hector/shared-types';
import type { ProviderPreview } from './apercuDePlan';
import type { MarqueDeParagraphe } from '@hector/docx-forme';
import type { EtapeDePlan } from './planDeFormatText';
import { causeBornee, type RefusDeLEditeur } from './recuDEcritureDeForme';

/** Une étape que l'éditeur refuse, avec ce qu'il en dit. */
export interface EtapeRefusee {
  stepId: string;
  refus: RefusDeLEditeur;
}

/** Pourquoi un paragraphe, ou un passage, est sauté : la raison du contrat, et la cause de l'éditeur quand elle seule l'explique. */
export interface RaisonDeSaut {
  reason: CanvasFormatEditorRefusalReason;
  detail?: string;
  /**
   * Présent quand l'éditeur n'a rien refusé : le paragraphe est épargné
   * d'avance (`ecrireOuEpargnerLesRevisionsDeFormeDeParagraphe`,
   * `ecritureDeForme.ts`), l'écriture n'a pas été soumise. Le reçu le dit pour
   * ce que c'est.
   */
  epargne?: true;
}

/**
 * Ce qu'on fait d'un aperçu INVALIDE. `retirer` : l'éditeur nomme les étapes
 * qu'il refuse, le plan est aperçu de nouveau sans elles. `refuser` : il n'en
 * nomme aucune du lot — rien ne dit quoi retirer, l'appel est refusé.
 */
export type SuiteDUnApercuInvalide =
  | { issue: 'retirer'; refusees: EtapeRefusee[]; gardees: EtapeDePlan[] }
  | { issue: 'refuser'; cause: string };

/**
 * Décide la suite d'un aperçu que l'éditeur dit invalide.
 *
 * @param steps Les étapes du plan aperçu.
 * @param echecs Les échecs que l'aperçu nomme ; l'éditeur les donne tous en une passe.
 */
export function suiteDUnApercuInvalide(steps: readonly EtapeDePlan[], echecs: ProviderPreview['failures']): SuiteDUnApercuInvalide {
  const [premier] = echecs ?? [];
  if (echecs === undefined || premier === undefined) return { issue: 'refuser', cause: 'the editor named no cause' };
  const duLot = new Set(steps.map((step) => step.id));
  // Bornée : la cause part dans `detail`, que le contrat du reçu limite — un long message ferait rejeter le reçu entier.
  const causeDe = (echec: typeof premier): string => causeBornee(`${echec.code}: ${echec.message ?? 'the editor gave no message'}`);
  const etranger = echecs.find((echec) => !duLot.has(echec.stepId));
  if (etranger !== undefined) return { issue: 'refuser', cause: `${causeDe(etranger)} (on a step that is not part of the plan: ${etranger.stepId})` };
  const refusees = new Map<string, EtapeRefusee>();
  for (const echec of echecs) {
    if (!refusees.has(echec.stepId)) refusees.set(echec.stepId, { stepId: echec.stepId, refus: { code: echec.code, detail: causeDe(echec) } });
  }
  return { issue: 'retirer', refusees: [...refusees.values()], gardees: steps.filter((step) => !refusees.has(step.id)) };
}

/**
 * Les codes d'un refus qui porte sur le DOCUMENT, pas sur une étape : rejouer
 * le lot par morceaux rencontrerait le même refus à chaque morceau.
 * `REVISION_MISMATCH` : le document a changé sous l'écriture (`mesure-repli-de-lot`,
 * L5). `PERMISSION_DENIED` : l'éditeur est en lecture seule (`refusPassager.ts`).
 */
const REFUS_DU_DOCUMENT: ReadonlySet<string> = new Set(['REVISION_MISMATCH', 'PERMISSION_DENIED']);

/**
 * Ce qu'on fait d'un plan que l'éditeur refuse à l'APPLICATION. `arreter` : le
 * refus porte sur le document, ou l'éditeur ne donne pas de code — rien n'est
 * rejoué. `redecouper` : le refus ne dit pas quelle étape est en cause, les
 * deux moitiés sont rejouées, la première d'abord. `sauter` : l'étape est
 * seule, c'est elle que l'éditeur refuse. `sans_objet` : seule, elle ne
 * changerait rien.
 */
export type SuiteDUnRefus =
  | { issue: 'arreter' }
  | { issue: 'redecouper'; moities: [EtapeDePlan[], EtapeDePlan[]] }
  | { issue: 'sauter'; stepId: string }
  | { issue: 'sans_objet'; stepId: string };

/**
 * Décide la suite d'un plan refusé à l'application. Le plan refusé n'a rien
 * écrit (en-tête de `ecritureDeForme.ts`) : aucune de ses étapes n'est rejouée
 * après avoir été appliquée.
 */
export function suiteDUnRefusALApplication(steps: readonly EtapeDePlan[], refus: RefusDeLEditeur): SuiteDUnRefus {
  if (refus.code === undefined || REFUS_DU_DOCUMENT.has(refus.code)) return { issue: 'arreter' };
  const [seule, ...autres] = steps;
  if (seule === undefined) return { issue: 'arreter' };
  if (autres.length === 0) return { issue: refus.code === 'NO_OP' ? 'sans_objet' : 'sauter', stepId: seule.id };
  const milieu = Math.ceil(steps.length / 2);
  return { issue: 'redecouper', moities: [steps.slice(0, milieu), steps.slice(milieu)] };
}

/**
 * Traduit le refus de l'éditeur en une raison que l'agent peut redire.
 *
 * L'éditeur dit la FAMILLE du refus ; le fichier dit ce que le paragraphe
 * porte. Une raison précise n'est rendue que si les deux concordent : un signet
 * au fichier n'explique pas un refus qui ne parle pas de marqueur. Sinon la
 * cause de l'éditeur est rendue telle quelle (`refused_by_editor`).
 *
 * Refus relevés par `mesure-repli-de-lot` (SuperDoc 2.15.1, 2026-10-02) :
 * - `INVALID_CONTEXT`, « format selection straddles a bookmark, protection, or
 *   move-range marker. » : un signet sur une part du texte, un signet à cheval,
 *   un texte déplacé en attente, une plage de permission (L6) — l'éditeur ne
 *   dit pas lequel ; la plage de permission n'a pas de raison au contrat, elle
 *   est rendue avec les mots de l'éditeur ;
 * - `INVALID_CONTEXT`, « unsupported-context » : l'étape couvre un changement
 *   de forme de texte en attente (L6, L9) ;
 * - `CAPABILITY_UNAVAILABLE`, « format selection intersects a structured
 *   document tag. » : un contrôle de contenu (L6) ;
 * - `REVISION_MISMATCH` à la compilation d'un aperçu, document inchangé : une
 *   étape par texte derrière un appel de note, ou dans un paragraphe dont du
 *   texte est déplacé en attente (L6, L13).
 *
 * @param porte Ce que le fichier dit du paragraphe de l'étape refusée.
 */
export function raisonDuRefus(refus: RefusDeLEditeur, porte: readonly MarqueDeParagraphe[]): RaisonDeSaut {
  if (refus.code === 'INVALID_CONTEXT' && /bookmark|move-range/i.test(refus.detail)) {
    if (porte.includes('deplacement_en_attente')) return { reason: 'pending_tracked_change' };
    if (porte.includes('signet_a_cheval')) return { reason: 'bookmark_across_paragraphs' };
    if (porte.includes('signet')) return { reason: 'bookmark' };
  } else if (refus.code === 'INVALID_CONTEXT' && porte.includes('revision_de_forme_de_texte')) {
    return { reason: 'pending_tracked_change' };
  } else if (refus.code === 'CAPABILITY_UNAVAILABLE' && /structured document tag/i.test(refus.detail)) {
    return { reason: 'content_control' };
  } else if (refus.code === 'REVISION_MISMATCH') {
    return { reason: 'passage_not_addressable' };
  }
  return { reason: 'refused_by_editor', detail: refus.detail };
}

/**
 * Les marques devant lesquelles l'éditeur a refusé, à l'application et sans que
 * l'aperçu l'ait vu, une étape de forme de texte (`mesure-repli-de-lot`, L6, L8,
 * L9, rejoué le 2026-10-04) : une étape qui couvre tout le texte du paragraphe,
 * ou un passage — celui dont la forme a changé sous suivi, celui que le
 * contrôle verrouille, celui qui touche une borne de signet (L8 : le morceau
 * qui suit le signet, celui qui enjambe son début). Une plage de permission
 * est refusée comme un signet (L6, même message).
 */
const MARQUES_A_RISQUE: ReadonlySet<MarqueDeParagraphe> = new Set([
  'signet',
  'signet_a_cheval',
  'deplacement_en_attente',
  'revision_de_forme_de_texte',
  'controle_verrouille',
  'plage_de_permission',
]);

/**
 * Vrai si une étape de forme de texte sur ce paragraphe risque d'être refusée
 * à l'application. Ce n'est qu'un SOUPÇON, qui décide de l'ordre des écritures
 * ({@link isolerLesEtapes}) : l'éditeur seul dit si l'étape est refusée.
 *
 * WHY la même liste pour tout le texte et pour un passage : un passage loin du
 * signet est écrit (L6, L8), mais rien ici ne dit où il est par rapport à lui.
 * Le soupçon à tort coûte une entrée d'historique ; le refus manqué, en cours
 * de lot, arrête le peintre de l'éditeur (en-tête de `ecritureDeForme.ts`).
 */
export function risqueUnRefusDeTexte(porte: readonly MarqueDeParagraphe[]): boolean {
  return porte.some((marque) => MARQUES_A_RISQUE.has(marque));
}

/**
 * Sort de leurs lots les étapes à risque : chacune devient un plan d'UNE étape,
 * soumis avant les lots, qui partent sans elles.
 *
 * WHY : un plan refusé après avoir joué une étape arrête le peintre de
 * l'éditeur (en-tête de `ecritureDeForme.ts`, « La page après un plan
 * refusé »). Un plan d'une étape, refusé, n'a rien joué : il n'arrête rien.
 * Soumettre à part ce que l'éditeur risque de refuser garde les lots à l'abri
 * de ce refus. Le prix : une étape isolée que l'éditeur accepte est sa propre
 * entrée d'historique, donc sa propre annulation.
 */
export function isolerLesEtapes(lots: ReadonlyArray<readonly EtapeDePlan[]>, aRisque: (etape: EtapeDePlan) => boolean): EtapeDePlan[][] {
  return [
    ...lots.flatMap((lot) => lot.filter(aRisque).map((etape) => [etape])),
    ...lots.map((lot) => lot.filter((etape) => !aRisque(etape))).filter((lot) => lot.length > 0),
  ];
}

/**
 * Ce qu'un plan refusé à l'application dit de lui-même par la révision du
 * document, relue après le refus (`mesure-repli-de-lot`, L11 ;
 * `mesure-gel-de-peinture`, D1 à D10) :
 * - `rien_de_joue` : la révision n'a pas bougé — refus à la première étape, ou
 *   dès la compilation ;
 * - `etapes_jouees` : elle a avancé du rang de l'étape refusée plus un, donc de
 *   2 au moins et du nombre d'étapes du plan au plus. Des étapes ont été jouées
 *   puis défaites : le peintre de l'éditeur est arrêté ;
 * - `document_change` : aucune des deux — elle a reculé, avancé de 1, avancé de
 *   plus que le plan n'a d'étapes, ou ne se lit pas comme un entier. Le refus
 *   seul ne l'explique pas : l'avocat a tapé, ou une autre écriture est passée.
 *
 * ⚠️ Une frappe venue pendant un plan refusé à un rang bas reste sous la borne
 * et se lit `etapes_jouees` : la borne resserre la fenêtre, elle ne la ferme pas.
 */
export type AvanceApresUnRefus = 'rien_de_joue' | 'etapes_jouees' | 'document_change';

/**
 * Qualifie l'avance de la révision du document autour d'un plan refusé.
 *
 * @param avant La révision sous laquelle le plan a été soumis.
 * @param apres La révision relue après le refus.
 * @param etapes Le nombre d'étapes du plan refusé.
 */
export function avanceApresUnRefus(avant: string, apres: string, etapes: number): AvanceApresUnRefus {
  if (apres === avant) return 'rien_de_joue';
  const estUnEntier = (revision: string): boolean => /^\d+$/.test(revision);
  if (!estUnEntier(avant) || !estUnEntier(apres)) return 'document_change';
  const avance = Number(apres) - Number(avant);
  return avance >= 2 && avance <= etapes ? 'etapes_jouees' : 'document_change';
}
